import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { SITES_KEY, createSite, submitSite, uploadMedia, type PendingMedia, type SiteInput } from './api';

/**
 * CR-016 B3 – Báo dự án khi mất mạng (DEC-267). Công trường, đường quê hay mất sóng: bấm Gửi lúc không có mạng thì
 * cả phiếu (thông tin + ảnh + video) lưu vào IndexedDB của máy, có mạng lại thì tự gửi theo đúng thứ tự
 * tạo → tải ảnh → gửi. Mỗi phiếu gắn người báo: máy dùng chung thì chỉ gửi phiếu của người đang đăng nhập.
 * Gửi dở (đã tạo dự án, đã tải vài ảnh) thì nhớ siteId / uploadedId, lần sau không tạo trùng, không tải lại.
 */
export interface QueuedSiteReport {
  key: string;
  userId: number;
  savedAt: string;
  body: SiteInput;
  media: PendingMedia[];
  siteId: number | null;
  /** Lỗi không phải do mạng (vd dữ liệu sai) – không tự gửi lại, chờ người báo mở xem. */
  lastError: string | null;
}

const DB = 'mt_offline';
const STORE = 'site_reports';
const CHANGED = 'mt-site-queue-changed';

function openDb(): Promise<IDBDatabase> {
  return new Promise((ok, fail) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'key' });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error ?? new Error('IndexedDB'));
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((ok, fail) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => ok(req.result);
      req.onerror = () => fail(req.error ?? new Error('IndexedDB'));
    });
  } finally {
    db.close();
  }
}

const changed = () => window.dispatchEvent(new Event(CHANGED));

export async function listQueued(userId: number): Promise<QueuedSiteReport[]> {
  if (typeof indexedDB === 'undefined') return [];
  const all = await tx<QueuedSiteReport[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedSiteReport[]>);
  return all.filter((r) => r.userId === userId).sort((a, b) => a.savedAt.localeCompare(b.savedAt));
}

async function put(r: QueuedSiteReport) {
  await tx('readwrite', (s) => s.put(r));
}

async function remove(key: string) {
  await tx('readwrite', (s) => s.delete(key));
}

export async function enqueueReport(r: Omit<QueuedSiteReport, 'key' | 'savedAt' | 'lastError'>) {
  await put({ ...r, key: `site-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, savedAt: new Date().toISOString(), lastError: null });
  changed();
}

export async function discardQueued(key: string) {
  await remove(key);
  changed();
}

/** Lỗi mạng (mất sóng, máy chủ không tới được) → giữ phiếu chờ gửi; lỗi khác → báo người dùng. */
export const isNetworkError = (e: unknown) => (e instanceof ApiError && e.status === 0) || e instanceof TypeError;

let flushing: Promise<string[]> | null = null;

/**
 * Gửi lần lượt các phiếu chờ của người dùng. Trả về mã dự án đã gửi được. Gặp lỗi mạng thì dừng (phiếu sau cũng sẽ lỗi);
 * lỗi khác thì ghi lastError cho phiếu đó rồi gửi phiếu kế.
 */
export function flushQueued(userId: number): Promise<string[]> {
  if (flushing) return flushing;
  flushing = (async () => {
    const sent: string[] = [];
    for (const r of await listQueued(userId)) {
      if (r.lastError) continue;
      try {
        if (!r.siteId) {
          r.siteId = (await createSite(r.body)).id;
          await put(r);
        }
        for (const m of r.media) {
          if (m.uploadedId) continue;
          m.uploadedId = (await uploadMedia(r.siteId, m)).id;
          await put(r);
        }
        sent.push((await submitSite(r.siteId)).code);
        await remove(r.key);
      } catch (e) {
        if (isNetworkError(e)) break;
        r.lastError = (e as Error).message;
        await put(r);
      }
    }
    changed();
    return sent;
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

/** Danh sách phiếu chờ gửi của người đang đăng nhập (tự làm mới khi hàng chờ đổi). */
export function useQueuedReports() {
  const { user } = useAuth();
  const [items, setItems] = useState<QueuedSiteReport[]>([]);
  const refresh = useCallback(() => {
    if (!user) return setItems([]);
    listQueued(user.id)
      .then(setItems)
      .catch(() => setItems([]));
  }, [user]);
  useEffect(() => {
    refresh();
    window.addEventListener(CHANGED, refresh);
    return () => window.removeEventListener(CHANGED, refresh);
  }, [refresh]);
  return items;
}

/**
 * Gắn một lần trong khung ứng dụng: mở app, có mạng lại, và mỗi phút (khi còn phiếu chờ) thì thử gửi.
 * Gửi được thì báo "Đã gửi báo dự án DA…".
 */
export function useSiteQueueSync() {
  const { user, offline } = useAuth();
  const qc = useQueryClient();
  const userId = offline ? null : (user?.id ?? null);
  useEffect(() => {
    if (!userId || typeof indexedDB === 'undefined') return;
    const run = () => {
      if (!navigator.onLine) return;
      flushQueued(userId)
        .then((codes) => {
          if (!codes.length) return;
          void qc.invalidateQueries({ queryKey: SITES_KEY });
          toast.success(`Có mạng lại: đã gửi báo dự án ${codes.join(', ')}. Kinh doanh thị trường đã nhận thông báo.`);
        })
        .catch(() => undefined);
    };
    run();
    window.addEventListener('online', run);
    const t = setInterval(run, 60_000);
    return () => {
      window.removeEventListener('online', run);
      clearInterval(t);
    };
  }, [userId, qc]);
}
